import { useMemo, useState } from 'react';
import { AlertTriangle, Image as ImageIcon, Search } from 'lucide-react';
import { MediaDropZone } from '@/components/media/MediaDropZone';
import { MediaThumbnail } from '@/components/media/MediaThumbnail';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useMediaLibrary } from '@/hooks/useMediaLibrary';
import { isUploaded, publicUrl, type MediaItem } from '@/lib/media';
import { mediaMarkup, pendingMediaUrl, defaultMediaLabel, type MarkupFormat } from '@/lib/mediaMarkup';
import { cn } from '@/lib/utils';

interface MediaPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The format the markup should be generated for. */
  format: MarkupFormat;
  /** Called with the finished markup string, ready to insert at the cursor. */
  onInsert: (markup: string) => void;
}

/**
 * Inserts an image (or links a file) from the media library into an
 * article/section, or lets the user add a new file and insert that.
 *
 * For a local-only file, the generated markup uses a `wikistar-media:`
 * placeholder URL rather than a fake address. That placeholder is
 * deliberately unresolvable: it can't accidentally render as a working
 * image, and `sanitizeUrl()` will refuse it, so the preview honestly
 * shows "not uploaded yet" instead of looking fine. At publish time,
 * every placeholder is uploaded and rewritten to the real Blossom URL —
 * see `useResolvePendingMedia` — so the signed content never ships one.
 */
export function MediaPickerDialog({ open, onOpenChange, format, onInsert }: MediaPickerDialogProps) {
  const { items, isLoading } = useMediaLibrary();

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<MediaItem>();
  const [alt, setAlt] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items ?? [];
    return (items ?? []).filter((item) =>
      `${item.name} ${item.alt ?? ''} ${item.tags.join(' ')}`.toLowerCase().includes(needle),
    );
  }, [items, query]);

  const reset = () => {
    setSelected(undefined);
    setAlt('');
    setQuery('');
  };

  const choose = (item: MediaItem) => {
    setSelected(item);
    // Default to the saved alt text; if there isn't one, leave the field
    // empty so the nudge stays visible rather than being pre-filled away.
    setAlt(item.alt ?? '');
  };

  const insert = () => {
    if (!selected) return;
    const label = alt.trim() || defaultMediaLabel(selected);
    const url = publicUrl(selected) ?? pendingMediaUrl(selected.sha256);
    onInsert(mediaMarkup(selected.kind, url, label, format));
    onOpenChange(false);
    reset();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) reset();
      }}
    >
      {/* An explicit aria-describedby quiets the Radix warning by naming
          the description below rather than leaving it unlinked. */}
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden" aria-describedby="media-picker-desc">
        <DialogHeader>
          <DialogTitle>Insert media</DialogTitle>
          <DialogDescription id="media-picker-desc">
            Pick something from your library, or add a new file. It'll be uploaded automatically when you publish if it
            isn't already.
          </DialogDescription>
        </DialogHeader>

        {selected ? (
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <MediaThumbnail item={selected} className="w-28 shrink-0" />
              <div className="min-w-0 flex-1 space-y-2">
                <p className="truncate text-sm font-medium" title={selected.name}>
                  {selected.name}
                </p>
                {!isUploaded(selected) && (
                  <Badge variant="outline" className="text-xs font-normal">
                    Local only — will upload on publish
                  </Badge>
                )}
              </div>
            </div>

            {/* Nudge, not requirement — but if the library already has alt
                text for this file, the field above was pre-filled with it,
                so this only shows when it's genuinely missing. */}
            <div className="space-y-1.5">
              <Label htmlFor="insert-alt">
                {selected.kind === 'image' ? 'Alt text' : 'Link text'}
              </Label>
              <Input
                id="insert-alt"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
                placeholder={selected.kind === 'image' ? 'Describe this image…' : 'What should the link say?'}
                autoFocus
              />
              {!alt.trim() && selected.kind === 'image' && (
                <p className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-500">
                  <AlertTriangle className="size-3.5 shrink-0" />
                  No alt text — screen readers won't know what this shows.
                </p>
              )}
              {selected.kind !== 'image' && (
                <p className="text-xs text-muted-foreground">
                  {selected.kind === 'image'
                    ? ''
                    : `A ${selected.kind === 'pdf' ? 'PDF' : selected.kind} is inserted as a link, not an embed.`}
                </p>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setSelected(undefined)}>
                Back
              </Button>
              <Button type="button" onClick={insert}>
                <ImageIcon className="size-4" />
                Insert
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex min-h-0 flex-col gap-4">
            <MediaDropZone onAdded={choose} />

            <Separator />

            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search your library…"
                className="pl-9"
              />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              {isLoading ? (
                <p className="py-8 text-center text-sm text-muted-foreground">Loading your library…</p>
              ) : filtered.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  {(items?.length ?? 0) === 0
                    ? 'Your library is empty — add a file above to get started.'
                    : 'Nothing matches that search.'}
                </p>
              ) : (
                <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {filtered.map((item) => (
                    <li key={item.sha256}>
                      <button
                        type="button"
                        onClick={() => choose(item)}
                        className={cn(
                          'w-full rounded-lg text-left ring-offset-background transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        )}
                        title={item.name}
                      >
                        <MediaThumbnail item={item} className="transition-shadow hover:shadow-md" />
                        <p className="mt-1 truncate text-xs">{item.name}</p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
